import { motion } from "framer-motion";
import { Phone, Mail } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export const FADE_UP = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" } };

export const Reveal = ({ index = 0, className, children }) => (
  <motion.article
    {...FADE_UP}
    transition={{ duration: 0.8, delay: (index % 3) * 0.1, ease: EASE }}
    className={className}
  >
    {children}
  </motion.article>
);

export const ProductCta = ({ heading = "Ready to see it on your desk?" }) => (
  <section className="relative bg-navy pb-24 md:pb-32 pt-8" data-testid="product-cta">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="border border-steel/30 bg-navy-deep/60 p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8 md:gap-12"
      >
        <div className="flex-1">
          <h2 className="font-display font-bold tracking-tight text-2xl md:text-3xl text-fog">{heading}</h2>
          <p className="text-sm md:text-base text-fog/60 leading-relaxed mt-3 max-w-xl">
            Tell us about your operation and someone from our team — not a ticket queue — will
            walk you through it within one business day.
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          <a
            href="mailto:sales@northwesttitlealliance.com"
            data-testid="product-cta-email"
            className="group relative px-7 py-4 bg-tech text-fog font-mono-accent text-[11px] tracking-[0.22em] uppercase overflow-hidden"
          >
            <span className="absolute inset-0 bg-tech-bright translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
            <span className="relative flex items-center gap-2">
              <Mail className="h-4 w-4" /> Talk to Sales
            </span>
          </a>
          <a
            href="tel:+12534531754"
            data-testid="product-cta-phone"
            className="px-7 py-4 border border-steel text-fog/80 hover:border-tech-bright hover:text-tech-bright font-mono-accent text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 flex items-center gap-2"
          >
            <Phone className="h-4 w-4" /> (253) 453-1754
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

export const PageHero = ({ kicker, title, accent, sub, children }) => (
  <section className="relative pt-36 md:pt-44 pb-12 md:pb-16 grain">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
        <span className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-tech-bright">{kicker}</span>
        <h1 className="font-display font-bold tracking-[-0.03em] leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-fog mt-6">
          {title}
          <span className="text-tech">.</span>
          {accent && (
            <>
              <br />
              <span className="text-fog/40">{accent}</span>
            </>
          )}
        </h1>
        {sub && <p className="text-base md:text-lg text-fog/60 leading-relaxed max-w-2xl mt-6">{sub}</p>}
        {children}
      </motion.div>
    </div>
  </section>
);
